import { useMemo, useState } from 'react';

import { useCurrentEventQuery } from '../../../event/hooks/useCurrentEventQuery';
import { useCurrentMilestone } from '../../../event/hooks/useCurrentMilestone';
import { useCurrentUserQuery } from '../../../user/hooks/useCurrentUserQuery';
import { useEventBedroomsQuery } from '../../hooks/useEventBedroomsQuery';
import { useJoinBedroomMutation } from '../../hooks/useJoinBedroomMutation';
import { useLeaveBedroomMutation } from '../../hooks/useLeaveBedroomMutation';

export type VisibleUsersType = 'ALL' | 'INVALID' | 'VALID';

export const useBedrooms = (isAdminView = false) => {
  const [selectedBedroomId, setSelectedBedroomId] = useState<string | null>(
    null,
  );
  const { data: currentUserData } = useCurrentUserQuery();
  const { data: currentEventData } = useCurrentEventQuery();
  const currentEvent = currentEventData?.currentEvent;
  const { data: eventBedroomsData } = useEventBedroomsQuery(
    currentEvent?.id ?? null,
  );
  const currentMilestone = useCurrentMilestone(currentEvent?.milestones ?? []);

  const { joinBedroom } = useJoinBedroomMutation();
  const { leaveBedroom } = useLeaveBedroomMutation();

  const bedrooms = useMemo(
    () => eventBedroomsData?.eventBedrooms ?? [],
    [eventBedroomsData?.eventBedrooms],
  );

  const onJoinBedroom = (bedroomId: string) => {
    joinBedroom(bedroomId, currentEvent?.id ?? '');
  };
  const onLeaveBedroom = (bedroomId: string) => {
    leaveBedroom(bedroomId, currentEvent?.id ?? '');
  };

  const joinedBedroomId = useMemo(() => {
    if (currentEvent?.hasPaidBedroom) {
      return (
        bedrooms.find((bedroom) =>
          bedroom.users.some(
            (user) => user.id === currentUserData?.currentUser?.id,
          ),
        )?.id ?? null
      );
    }
    return null;
  }, [currentEvent, currentUserData, bedrooms]);

  const onCloseBedroomUsersDrawer = () => {
    setSelectedBedroomId(null);
  };

  const onOpenBedroomUsersDrawer = (bedroomId: string) => {
    setSelectedBedroomId(bedroomId);
  };

  const isStaff = currentUserData?.currentUser?.isStaff ?? false;
  const isAdmin = currentUserData?.currentUser?.isAdmin ?? false;
  const canAccessBedrooms =
    (currentEvent?.hasPaidBedroom ?? false) || (isAdminView && isAdmin);
  const canManageBedroomUsers = isStaff || (isAdminView && isAdmin);

  const selectedBedroom = useMemo(() => {
    return bedrooms.find((bedroom) => bedroom.id === selectedBedroomId) ?? null;
  }, [bedrooms, selectedBedroomId]);

  return {
    bedrooms,
    onJoinBedroom,
    onLeaveBedroom,
    joinedBedroomId,
    hasPaidBedroom: currentEvent?.hasPaidBedroom ?? false,
    currentMilestone,
    currentEvent,
    onCloseBedroomUsersDrawer,
    selectedBedroom,
    canAccessBedrooms,
    canManageBedroomUsers,
    onOpenBedroomUsersDrawer,
  };
};
